import { createElement } from 'react';
import { CountryCode, CountryName, CountryNameKey, ShippingAddress } from '@dayetopia/types';
import { WindowLocation } from '@reach/router';
import LinkTranslated from '@components/shared/LinkTranslated';
import { DEFAULT_KLAVIYO_LIST_ID, DEFAULT_KLAVIYO_LIST_ID_US } from '@constants';
import { PageOptions, ApplePayShippingContact, CartItem, CountryTuple, GooglePayShippingContact, ProductCode } from '@contracts';
import { removeLocalesFromPath } from './language';

export const capitalizeFirstLetter = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

export const resetAddressKeepCountry = (address: ShippingAddress) => {
  const { country } = address;
  return { country };
};

export const pageIsAccountPage = (locationPath: string) =>
  locationPath.includes(`/${PageOptions.Dashboard}/`) || locationPath.includes(`/${PageOptions.Account}/`);

export const pageIsLoginPage = (locationPath: string) => locationPath.includes(`/${PageOptions.Dashboard}/${PageOptions.Login}`);

export const pageIsFocusPage = (locationPath: string) =>
  locationPath.includes('/login') ||
  locationPath.includes('/reset-password') ||
  locationPath.includes('/forgot-password') ||
  locationPath.includes('/activate-test') ||
  locationPath.includes('/enable-new-features') ||
  locationPath.includes('/request-treatment') ||
  locationPath.includes('/update-health-context') ||
  locationPath.includes('dashboard/book-a-call') ||
  locationPath.includes('/reactivate-subscription') ||
  locationPath.includes('/ppc-questionnaire') ||
  locationPath.includes('/update-billing');

export const pageIsQuestionnairePage = (locationPath: string) => locationPath.includes('/ppc-questionnaire');

/**
 * Checks if the given pathname ends with '/ads' or '/ads/'.
 *
 * @param {string} pathname - The pathname to check.
 * @returns {boolean} True if the pathname ends with '/ads' or '/ads/', otherwise false.
 */
export const pageIsAdsPage = (pathname: string): boolean => {
  return pathname.endsWith('/ads') || pathname.endsWith('/ads/');
};

export const shuffleArray = (array: any[]) => {
  const shuffled = [...array];
  let currentIndex = array.length;
  let randomIndex;

  // While there remain elements to shuffle.
  while (currentIndex !== 0) {
    // Pick a remaining element.
    randomIndex = Math.floor(Math.random() * currentIndex);
    currentIndex -= 1;

    // And swap it with the current element.
    [shuffled[currentIndex], shuffled[randomIndex]] = [shuffled[randomIndex], shuffled[currentIndex]];
  }

  return shuffled;
};

export const getYouTubeEmbedUrl = (videoUrl: string): string | null => {
  const youtubeRegex = /^(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]{11})/i;
  const match = videoUrl.match(youtubeRegex);

  if (match && match[1]) {
    const videoId = match[1];
    return `https://www.youtube.com/embed/${videoId}`;
  }

  return null;
};

export const isEmptyObject = (obj) => Object.keys(obj).length === 0 && obj.constructor === Object;

export const convertApplePayAddressToRecurly = (shippingAddress: ApplePayShippingContact): ShippingAddress => {
  const street1 = shippingAddress.addressLines && shippingAddress.addressLines.length > 0 ? shippingAddress.addressLines[0] : '';
  const street2 = shippingAddress.addressLines && shippingAddress.addressLines.length > 1 ? shippingAddress.addressLines[1] : '';

  return {
    firstName: shippingAddress.givenName,
    lastName: shippingAddress.familyName,
    street1,
    street2,
    city: shippingAddress.locality,
    region: shippingAddress.administrativeArea,
    postalCode: shippingAddress.postalCode,
    country: shippingAddress.countryCode,
    phone: shippingAddress.phoneNumber
  };
};

export const convertGooglePayAddressToRecurly = (shippingAddress: GooglePayShippingContact): ShippingAddress => {
  const street1 = shippingAddress.address1.trim();
  const street2 = `${shippingAddress.address2 ?? ''}${shippingAddress.address3 ? ` ${shippingAddress.address3}` : ''}`.trim();
  const [firstName, ...lastNameParts] = shippingAddress.name.split(' ');
  const lastName = lastNameParts.join(' ');

  return {
    firstName,
    lastName,
    street1,
    street2,
    city: shippingAddress.locality,
    region: shippingAddress.administrativeArea,
    postalCode: shippingAddress.postalCode,
    country: shippingAddress.countryCode,
    phone: shippingAddress.phoneNumber || ''
  };
};

export const isCountryMatch = (treatmentCountry: string, countryCode: CountryCode): boolean => {
  if (treatmentCountry.startsWith('-')) {
    return treatmentCountry.slice(1) !== countryCode;
  }
  return treatmentCountry === countryCode;
};

export const getSearchParameterByName = <T>(name: string, location: WindowLocation): T => {
  const query = new URLSearchParams(location.search);

  return query.get(name) as any;
};

export function addSearchParameters(url: string, parameters: { [key: string]: string }): string {
  let newUrl = url;

  Object.keys(parameters).forEach((key) => {
    newUrl = addSearchParameter(newUrl, key, parameters[key]);
  });

  return newUrl;
}

export function addPathParameter(url: string, param: string | number): string {
  const [path, search] = url.split('?');
  const updatedPath = path.endsWith('/') ? path : `${path}/`;

  return `${updatedPath}${param}${search ? `?${search}` : ''}`;
}

export function addSearchParameter(url: string, name: string, value: string): string {
  const query = new URLSearchParams(url);

  query.set(name, value);

  return query.toString();
}

export function findCountryKeyByName(countryName?: string): CountryCode | undefined {
  if (!countryName) {
    return undefined;
  }

  const countryKey = Object.keys(CountryName).find((key) => CountryName[key] === countryName);

  return (countryKey as CountryCode) || undefined;
}

export const isFestivePeriod = (): boolean => {
  const today = new Date();
  const month = today.getMonth();
  const day = today.getDate();

  const decemberMonth = 11;
  const januaryMonth = 0;

  return (month === decemberMonth && day >= 1) || (month === januaryMonth && day <= 3);
};

export const itemsContainDTProduct = (cartItems: CartItem[]): boolean =>
  cartItems.some(
    (item) =>
      item.productCode === ProductCode.VMSEssential ||
      item.productCode === ProductCode.VMSEssentialConsultation ||
      item.productCode === ProductCode.VaginalHealthBundle ||
      item.productCode === ProductCode.STIScreening ||
      item.productCode === ProductCode.VMSAdvanced ||
      item.productCode === ProductCode.ScreeningKit
  );

/*
 * Get icons array from prismic usp icons data
 */
export function getUspIconsFromData(sliceContainingIcons: any) {
  const icons = [];

  if (sliceContainingIcons?.icon_1?.gatsbyImageData) {
    icons.push({
      icon: sliceContainingIcons?.icon_1,
      icon_text: sliceContainingIcons?.icon_1_text
    });
  }

  if (sliceContainingIcons?.icon_2?.gatsbyImageData) {
    icons.push({
      icon: sliceContainingIcons?.icon_2,
      icon_text: sliceContainingIcons?.icon_2_text
    });
  }

  if (sliceContainingIcons?.icon_3?.gatsbyImageData) {
    icons.push({
      icon: sliceContainingIcons?.icon_3,
      icon_text: sliceContainingIcons?.icon_3_text
    });
  }

  return icons;
}

export const getSanitizedKey = (key = '', length = 20) => {
  let sanitizedKey = key.replace(/\s+/g, '_').toLowerCase();
  sanitizedKey = sanitizedKey.substring(0, length);
  return sanitizedKey;
};

export const createPreconfiguredMailtoLink = (
  supportEmail: string,
  supportEmailSubject: string,
  supportEmailBody: string,
  customerName: string,
  orderNumber: string
) => {
  const encodedSubject = encodeURIComponent(supportEmailSubject);

  // The function expects $customer_name and $order_number as variables in the supportEmailBody and replaces all occurrences with userInfo.firstName+userInfo.lastName and result.id resprectivelly
  const filledBody = supportEmailBody.replace(/\$customer_name/g, customerName).replace(/\$order_number/g, orderNumber);
  const encodedBody = encodeURIComponent(filledBody);

  return `mailto:${supportEmail}?subject=${encodedSubject}&body=${encodedBody}`;
};

export const hasAnyRelationsPrismic = (listOfRelations: { uid: string | null }[]) => {
  return listOfRelations.length > 0 && listOfRelations.every((item) => !!item.uid);
};

/**
 * Converts a string containing HTML anchor tags into an array of strings and React elements.
 * Anchor tags are transformed into clickable links that open in a new tab.
 *
 * @param {string} text - The input string that may contain HTML anchor tags
 * @returns {string | (string | JSX.Element)[]} Either the original string, an empty string if input is falsy, or an array of strings and React anchor elements
 * @example
 * // Returns: ['Hello ', <a href="https://example.com">click here</a>, ' to continue']
 * renderStringWithLinkTags('Hello <a href="https://example.com">click here</a> to continue')
 *
 * // Returns: 'String without tags'
 * renderStringWithLinkTags('String without tags')
 *
 * // Returns: ''
 * renderStringWithLinkTags('')
 */

export const renderStringWithLinkTags = (text: string): string | (string | JSX.Element)[] => {
  if (!text) return '';

  const parts = text.split(/(<a.*?<\/a>)/).map((part, index) => {
    if (part.startsWith('<a')) {
      const href = part.match(/href="(.*?)"/)?.[1];
      const replacedText = part.replace(/<[^>]+>/g, '');
      return createElement(
        LinkTranslated,
        {
          key: index,
          to: href,
          targetBlank: true
        },
        replacedText
      );
    }
    return part;
  });

  // Trim empty strings from start and end
  while (parts.length && parts[0] === '') parts.shift();
  while (parts.length && parts[parts.length - 1] === '') parts.pop();

  return parts;
};

/**
 * Returns the appropriate Klaviyo list ID based on the user's country.
 *
 * @param {CountryCode} country - The country code to check against
 * @returns {string} The Klaviyo list ID - returns US-specific list ID for US users, default list ID for all other countries
 */
export const getKlaviyoTrackingListId = (country: CountryCode) => {
  return country === CountryCode.US ? DEFAULT_KLAVIYO_LIST_ID_US : DEFAULT_KLAVIYO_LIST_ID;
};

/**
 * Updates or adds URL search parameters without page reload.
 *
 * @param {Object.<string, string | number>} searchParam - Object containing key-value pairs of search parameters to update or add
 * @param {WindowLocation} location - The current window location object
 * @returns {URLSearchParams} Updated URLSearchParams object with the new/modified parameters
 */
export const replaceOrUpdateURLParameter = (searchParam: { [key: string]: string | number }, location: WindowLocation): string => {
  const updatedSearchParams = new URLSearchParams(location.search);

  Object.keys(searchParam).forEach((key) => {
    updatedSearchParams.set(key, `${searchParam[key]}`);
  });

  return `?${updatedSearchParams.toString()}`;
};

/**
 * Prevents single words from appearing on the last line (orphan words)
 * by adding a non-breaking space between the last two words
 */
export const preventOrphanWords = (text: string) => {
  const words = text.split(' ');
  if (words.length < 2) return text;

  const lastTwoWords = words.slice(-2).join('\u00A0');
  return [...words.slice(0, -2), lastTwoWords].join(' ');
};

/**
 * Returns the top sorted keys from an object based on their values.
 *
 * @param {Object.<string, number>} obj - The object containing key-value pairs
 * @param {number} count - The number of top keys to return
 * @returns {string[]} An array of the top sorted keys
 */
export const returnTopSortedKeys = (obj: { [key: string]: number }, count: number): string[] => {
  const sortedArray = Object.entries(obj).sort((a, b) => b[1] - a[1]);
  const topKeys: string[] = sortedArray.slice(0, count).map(([key]) => key);

  return topKeys;
};

/**
 * Sorts an array of country codes with UK first, US second, and remaining countries alphabetically by name.
 *
 * @param {CountryTuple[]} countries - Array of country code tuples to sort
 * @param {string} [locale] - Optional locale string for proper localized sorting (e.g., 'en-GB', 'en-US')
 * @returns {CountryTuple[]} Sorted array of country code tuples
 */
export const sortCountries = (countries: CountryTuple[], locale?: string): CountryTuple[] => {
  const priorityOrder = [CountryCode.GB, CountryCode.US];

  return [...countries]
    .filter(([code]) => CountryName[code as CountryNameKey])
    .sort(([firstCode], [secondCode]) => {
      const firstIndex = priorityOrder.indexOf(firstCode as CountryCode);
      const secondIndex = priorityOrder.indexOf(secondCode as CountryCode);

      if (firstIndex !== secondIndex) {
        return (firstIndex === -1 ? priorityOrder.length : firstIndex) - (secondIndex === -1 ? priorityOrder.length : secondIndex);
      }

      return CountryName[firstCode as CountryNameKey].localeCompare(CountryName[secondCode as CountryNameKey], locale);
    });
};

/**
 * Returns a new array containing unique objects from the input array based on a specified key.
 *
 * @param {any[]} array - The input array of objects
 * @param {string} [key] - The key to check for uniqueness (default is 'id')
 * @returns {any[]} A new array with unique objects
 */
export const getUniqueObjects = (array: any[], key: string = 'id') => {
  return array.reduce((acc, obj) => {
    if (!acc.some((a: { [key: string]: any }) => a[key] === obj[key])) {
      acc.push(obj);
    }
    return acc;
  }, []);
};
